import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import { useNavigate } from 'react-router-dom';
import { usersApi, Media } from '../../../api/usersApi';
import { wallPostsApi, WallPost } from '../../../api/wallPostsApi';
import { getAvatarUrl, getMediaUrl } from '../../../utils/avatarUtils';
import { openFileInput } from '../../../utils/filePicker';
import config from '../../../config/config';
import { BottomSheet, BottomNavigation, NavItem } from '../../../shared/ui';
import CreatePost from './CreatePost';
import EditBio from './EditBio';
import Post from './Post';
import ImageViewer, { ImageViewerAction } from './ImageViewer';
import SitesScroll from './SitesScroll';
import { mergeSitesFromPosts, PostSite } from '../lib/getSitesFromPosts';
import PhotoGrid from './PhotoGrid';
import PostsFeed from './PostsFeed';
import ProfilePageSkeleton from './ProfilePageSkeleton';
import ProfileSettings from './ProfileSettings';
import { useProfileSiteFilter } from '../hooks/useProfileSiteFilter';
import styles from './ProfilePage.module.css';

function sumPhotosFromPosts(posts: WallPost[]): number {
  return posts.reduce((s, p) => s + (p.images?.length || 0), 0);
}
function sumLikesFromPosts(posts: WallPost[]): number {
  return posts.reduce((s, p) => s + (p.likes || 0), 0);
}

interface ProfilePageProps {
  hideHeader?: boolean;
  onBack?: () => void;
  userId?: number; // Опциональный ID пользователя для просмотра чужого профиля
  /** Не рендерить BottomNavigation (напр. профиль в оверлее дашборда — иначе два fixed-меню и «второе» уезжает при скролле) */
  hideBottomNavigation?: boolean;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ hideHeader = false, onBack, userId, hideBottomNavigation = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showMenu, hideMenu, pushBackLayer, popBackLayer } = useNavigation();
  // Используем userId из пропсов, если он передан, иначе используем текущего пользователя
  const profileUserId = userId !== undefined ? userId : user?.id;
  const isOwnProfile = !userId || userId === user?.id;
  
  // Инициализируем с данными из user контекста для моментального отображения
  const [profileUser, setProfileUser] = useState<{ id: number; name: string; bio?: string } | null>(
    profileUserId ? { id: profileUserId, name: userId === user?.id ? (user?.name || 'User') : 'User', bio: undefined } : null
  );
  const [avatar, setAvatar] = useState<Media | null>(null);
  const [coverImage, setCoverImage] = useState<Media | null>(null);
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const isLoadingRef = useRef(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const postsOffsetRef = useRef(0);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [allAvatars, setAllAvatars] = useState<Media[]>([]);
  const [showAvatarViewer, setShowAvatarViewer] = useState(false);
  const [avatarViewerIndex, setAvatarViewerIndex] = useState(0);
  const [avatarViewerSource, setAvatarViewerSource] = useState<HTMLElement | null>(null);
  const [showEditBio, setShowEditBio] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [coverFullLoaded, setCoverFullLoaded] = useState(false);
  const [showPostsFeed, setShowPostsFeed] = useState(false);
  const [feedInitialPostId, setFeedInitialPostId] = useState<number | null>(null);
  const [feedSiteId, setFeedSiteId] = useState<number | null | undefined>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  /** Все объекты из постов (накапливаются при загрузке), чтобы фильтр не терял варианты при выборе объекта */
  const [allSitesFromPosts, setAllSitesFromPosts] = useState<PostSite[]>([]);
  /** Общая статистика (по всем постам), не зависит от фильтра по объекту */
  const [totalStats, setTotalStats] = useState({ posts: 0, photos: 0, likes: 0 });
  const coverInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const coverFullImgRef = useRef<HTMLImageElement>(null);
  const loadProfileDataRef = useRef<(showLoading?: boolean, targetUserId?: number, postsOnly?: boolean) => Promise<void>>(() => Promise.resolve());
  const isPostsOnlyLoadingRef = useRef(false);

  const loadProfileData = useCallback(async (showLoading = false, targetUserId?: number, postsOnly = false) => {
    const targetId = targetUserId !== undefined ? targetUserId : profileUserId;
    if (!targetId || isLoadingRef.current) return;

    if (postsOnly) {
      if (isPostsOnlyLoadingRef.current) return;
      isPostsOnlyLoadingRef.current = true;
      const wasUnfiltered = selectedSiteId === null;
      try {
        const userPosts = await wallPostsApi.getUserPosts(targetId, 20, 0, selectedSiteId ?? undefined).catch(e => {
          console.warn('Failed to load posts:', e);
          return [];
        });
        const postsWithUserData: WallPost[] = userPosts.map(post => ({
          ...post,
          userName: profileUser?.name || 'User',
          userAvatar: avatar || undefined
        }));
        setPosts(postsWithUserData);
        setAllSitesFromPosts(prev => mergeSitesFromPosts(prev, userPosts));
        if (wasUnfiltered) {
          const list = userPosts as WallPost[];
          setTotalStats({
            posts: list.length,
            photos: sumPhotosFromPosts(list),
            likes: sumLikesFromPosts(list),
          });
        }
        postsOffsetRef.current = postsWithUserData.length;
        setHasMorePosts(postsWithUserData.length >= 20);
      } catch (error) {
        console.error('Ошибка загрузки постов:', error);
      } finally {
        isPostsOnlyLoadingRef.current = false;
      }
      return;
    }

    const shouldShowLoading = showLoading || !hasLoadedOnceRef.current;
    if (shouldShowLoading) {
      setIsLoadingPosts(true);
    }
    isLoadingRef.current = true;
    const wasUnfiltered = selectedSiteId === null;

    try {
      const [userData, avatarData, avatarsList, coverData, userPosts] = await Promise.all([
        usersApi.getUser(targetId),
        usersApi.getAvatar(targetId).catch(e => {
          console.warn('Failed to load avatar:', e);
          return null;
        }),
        usersApi.getUserAvatars(targetId).catch(e => {
          console.warn('Failed to load avatars list:', e);
          return [];
        }),
        usersApi.getCover(targetId).catch(e => {
          console.warn('Failed to load cover:', e);
          return null;
        }),
        wallPostsApi.getUserPosts(targetId, 20, 0, selectedSiteId ?? undefined).catch(e => {
          console.warn('Failed to load posts:', e);
          return [];
        })
      ]);

      setProfileUser({ id: userData.id, name: userData.name, bio: userData.bio });
      setAvatar(avatarData);
      setAllAvatars(avatarsList);
      setCoverImage(coverData);

      const postsWithUserData: WallPost[] = userPosts.map(post => ({
        ...post,
        userName: userData.name || 'User',
        userAvatar: avatarData || undefined
      }));
      setPosts(postsWithUserData);
      setAllSitesFromPosts(prev => mergeSitesFromPosts(prev, userPosts));
      if (wasUnfiltered) {
        const list = userPosts as WallPost[];
        setTotalStats({
          posts: list.length,
          photos: sumPhotosFromPosts(list),
          likes: sumLikesFromPosts(list),
        });
      }
      postsOffsetRef.current = userPosts.length;
      setHasMorePosts(userPosts.length >= 20);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      console.error('Ошибка загрузки данных профиля:', error);
    } finally {
      setIsLoadingPosts(false);
      isLoadingRef.current = false;
    }
  }, [profileUserId, isOwnProfile, user?.id, selectedSiteId, profileUser?.name, avatar]);

  loadProfileDataRef.current = loadProfileData;

  // Загружаем дополнительные посты при скролле
  const loadMorePosts = useCallback(async () => {
    if (!profileUserId || isLoadingMoreRef.current || !hasMorePosts || isPostsOnlyLoadingRef.current) return;

    isLoadingMoreRef.current = true;
    const wasUnfiltered = selectedSiteId === null;

    try {
      const batchSize = 20;
      const newPosts = await wallPostsApi.getUserPosts(
        profileUserId, 
        batchSize, 
        postsOffsetRef.current,
        selectedSiteId ?? undefined
      );
      
      if (newPosts.length === 0) {
        setHasMorePosts(false);
        return;
      }

      const postsWithUserData: WallPost[] = newPosts.map(post => ({
        ...post,
        userName: profileUser?.name || 'User',
        userAvatar: avatar || undefined
      }));

      const offset = postsOffsetRef.current;
      if (offset === 0) {
        setPosts(postsWithUserData);
      } else {
        setPosts(prev => [...prev, ...postsWithUserData]);
      }
      setAllSitesFromPosts(prev => mergeSitesFromPosts(prev, newPosts));
      if (wasUnfiltered) {
        const list = newPosts as WallPost[];
        setTotalStats(prev => ({
          posts: prev.posts + list.length,
          photos: prev.photos + sumPhotosFromPosts(list),
          likes: prev.likes + sumLikesFromPosts(list),
        }));
      }
      postsOffsetRef.current += newPosts.length;
      
      // Если загрузили меньше, чем запросили, значит больше нет
      if (newPosts.length < batchSize) {
        setHasMorePosts(false);
      }
    } catch (error) {
      console.error('Ошибка загрузки дополнительных постов:', error);
      setHasMorePosts(false);
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [profileUserId, profileUser?.name, avatar, selectedSiteId, hasMorePosts]);

  const resetPaginationAndReload = useCallback(() => {
    postsOffsetRef.current = 0;
    setHasMorePosts(true);
    if (profileUserId) loadProfileData(false, profileUserId, true);
  }, [profileUserId, loadProfileData]);

  useProfileSiteFilter(
    profileUserId ?? undefined,
    hasLoadedOnceRef,
    selectedSiteId,
    resetPaginationAndReload
  );

  // Сбрасываем фильтр по объекту, список объектов и общую статистику при переходе к другому пользователю
  const prevProfileUserIdRef = useRef(profileUserId);
  useEffect(() => {
    if (prevProfileUserIdRef.current !== profileUserId) {
      prevProfileUserIdRef.current = profileUserId;
      setSelectedSiteId(null);
      setAllSitesFromPosts([]);
      setTotalStats({ posts: 0, photos: 0, likes: 0 });
    }
  }, [profileUserId, setSelectedSiteId]);

  // Intersection Observer для автоматической загрузки при скролле
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMorePosts) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePosts && !isLoadingMoreRef.current) {
          loadMorePosts();
        }
      },
      {
        rootMargin: '200px' // Начинаем загрузку за 200px до появления триггера
      }
    );
    
    observer.observe(loadMoreTriggerRef.current);
    
    return () => {
      observer.disconnect();
    };
  }, [hasMorePosts, loadMorePosts]);

  // Посты уже приходят с API с учётом selectedSiteId, дополнительная фильтрация не нужна
  const postsWithImages = useMemo(
    () => posts.filter(post => post.images && post.images.length > 0),
    [posts]
  );

  // Получаем посты для ленты (фильтруем по siteId кликнутого поста)
  const feedPosts = useMemo(() => {
    if (feedSiteId === undefined) return []; // Если undefined, ничего не показываем
    if (feedSiteId === null) {
      // Показываем посты без привязки к объекту
      return posts.filter(post => !post.siteId);
    }
    // Показываем посты конкретного объекта
    return posts.filter(post => post.siteId === feedSiteId);
  }, [feedSiteId, posts]);

  // Получаем название объекта для ленты
  const feedSiteName = useMemo(() => {
    if (feedSiteId === undefined || feedSiteId === null) return null;
    const postWithSite = posts.find(post => post.siteId === feedSiteId);
    return postWithSite?.siteName || null;
  }, [feedSiteId, posts]);

  // Обработчик клика на пост в сетке
  const handleGridPostClick = useCallback((post: WallPost, imageIndex: number) => {
    setFeedInitialPostId(post.id);
    setFeedSiteId(post.siteId); // Сохраняем siteId поста
    setShowPostsFeed(true);
  }, []);

  // Закрытие ленты постов
  const handleCloseFeed = useCallback(() => {
    setShowPostsFeed(false);
    setFeedInitialPostId(null);
    setFeedSiteId(null);
  }, []);

  // Навигационные элементы для нижнего меню
  const navItems: NavItem[] = useMemo(() => [
    {
      id: 'ryaskovpro',
      label: 'Лента',
      icon: 'ri-home-line',
      path: '/dashboard',
      dashboardTab: 'ryaskovpro',
      onClick: () => {
        // Если открыта лента постов, закрываем её перед навигацией
        if (showPostsFeed) {
          handleCloseFeed();
        }
        navigate('/dashboard', { state: { tab: 'ryaskovpro' } });
      }
    },
    {
      id: 'projects',
      label: 'Проекты',
      icon: 'ri-building-line',
      path: '/dashboard',
      dashboardTab: 'projects',
      onClick: () => {
        // Если открыта лента постов, закрываем её перед навигацией
        if (showPostsFeed) {
          handleCloseFeed();
        }
        navigate('/dashboard', { state: { tab: 'projects' } });
      }
    },
    {
      id: 'statistics',
      label: 'Обзор',
      icon: 'ri-money-dollar-circle-line',
      path: '/dashboard',
      dashboardTab: 'statistics',
      onClick: () => {
        // Если открыта лента постов, закрываем её перед навигацией
        if (showPostsFeed) {
          handleCloseFeed();
        }
        navigate('/dashboard', { state: { tab: 'statistics' } });
      }
    },
    {
      id: 'profile',
      label: 'Профиль',
      icon: 'ri-user-line',
      path: '/profile',
      onClick: () => {
        if (onBack) {
          // Уже открыты как окно профиля, переход не нужен
          if (showPostsFeed) handleCloseFeed();
          return;
        }
        if (showPostsFeed) handleCloseFeed();
        else navigate('/profile');
      }
    }
  ], [showPostsFeed, handleCloseFeed, navigate, onBack]);

  // Сбрасываем флаг загрузки full при смене cover
  useEffect(() => {
    setCoverFullLoaded(false);
  }, [coverImage]);

  // Обновляем начальное состояние при изменении userId и загружаем данные в фоне (без loadProfileData в deps — иначе при смене фильтра эффект запускался бы повторно)
  useEffect(() => {
    if (profileUserId) {
      setProfileUser(prev => {
        if (!prev || prev.id !== profileUserId) {
          hasLoadedOnceRef.current = false;
          setIsLoadingPosts(true);
          postsOffsetRef.current = 0;
          setHasMorePosts(true);
          setShowAvatarViewer(false);
          setAvatarViewerIndex(0);
          return {
            id: profileUserId,
            name: (userId === user?.id || !userId) ? (user?.name || 'User') : 'User',
            bio: undefined
          };
        }
        if (isOwnProfile && prev.name !== user?.name) {
          return { ...prev, name: user?.name || 'User' };
        }
        return prev;
      });
      const isFirstLoad = !hasLoadedOnceRef.current;
      loadProfileDataRef.current(isFirstLoad, profileUserId);
    }
  }, [profileUserId, userId, user?.id, user?.name, isOwnProfile]);

  const handleBackToDashboard = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate('/dashboard');
    }
  }, [onBack, navigate]);

  // Если открыт как оверлей (onBack передан) — всегда кнопка назад.
  // Если открыт как самостоятельная страница и это свой профиль — кнопка настроек.
  useEffect(() => {
    if (onBack) {
      pushBackLayer(onBack, true);
      return () => { popBackLayer(); };
    }
    if (isOwnProfile) {
      showMenu(() => setShowSettings(true), true);
      return () => { hideMenu(); };
    }
  }, [showMenu, hideMenu, pushBackLayer, popBackLayer, isOwnProfile, onBack]);

  const handleCreatePost = async (content: string, images?: File[], siteId?: number | null) => {
    if (!isOwnProfile || !user?.id) return;

    try {
      const newPost = await wallPostsApi.createPost({
        content,
        siteId: siteId,
        images: images || [],
      });
      
      // Обновляем посты с правильными данными пользователя
      const postWithUserData: WallPost = {
        ...newPost,
        userName: profileUser?.name || user?.name || 'User',
        userAvatar: avatar || undefined
      };

      setPosts([postWithUserData, ...posts]);
      setTotalStats(prev => ({
        posts: prev.posts + 1,
        photos: prev.photos + (postWithUserData.images?.length || 0),
        likes: prev.likes + (postWithUserData.likes || 0),
      }));

      // Перезагружаем посты для получения актуальных данных (без skeleton)
      await loadProfileData(false, profileUserId);
    } catch (error) {
      console.error('Ошибка создания поста:', error);
      throw error; // Пробрасываем ошибку для обработки в CreatePost
    }
  };

  const handleLikePost = async (postId: number) => {
    try {
      let likeDelta = 0;
      // Обновляем состояние оптимистично
      setPosts(posts.map(post => {
        if (post.id === postId) {
          const wasLiked = post.isLiked;
          likeDelta = wasLiked ? -1 : 1;
          return {
            ...post,
            isLiked: !wasLiked,
            likes: wasLiked ? post.likes - 1 : post.likes + 1
          };
        }
        return post;
      }));
      if (likeDelta !== 0) {
        setTotalStats(prev => ({ ...prev, likes: Math.max(0, prev.likes + likeDelta) }));
      }

      // Отправляем запрос на сервер
      await wallPostsApi.toggleLike(postId);
    } catch (error) {
      console.error('Ошибка при лайке поста:', error);
      // Откатываем изменения в случае ошибки
      await loadProfileData(false, profileUserId);
    }
  };

  const handleDeletePost = async (postId: number) => {
    try {
      const deleted = posts.find(p => p.id === postId);
      // Удаляем из локального состояния оптимистично
      setPosts(posts.filter(post => post.id !== postId));
      if (deleted) {
        setTotalStats(prev => ({
          posts: Math.max(0, prev.posts - 1),
          photos: Math.max(0, prev.photos - (deleted.images?.length || 0)),
          likes: Math.max(0, prev.likes - (deleted.likes || 0)),
        }));
      }

      // Отправляем запрос на сервер
      await wallPostsApi.deletePost(postId);
    } catch (error) {
      console.error('Ошибка при удалении поста:', error);
      // Откатываем изменения в случае ошибки
      await loadProfileData(false, profileUserId);
    }
  };

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (userId: number): string => {
    const colors = [
      '#4A90E2', '#50C878', '#FF6B6B', '#FFA500', 
      '#9B59B6', '#E74C3C', '#3498DB', '#2ECC71'
    ];
    return colors[userId % colors.length];
  };

  const handleCoverImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openFileInput(coverInputRef.current);
  };

  const handleCoverImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isOwnProfile) return;
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (!isOwnProfile || !profileUserId) {
      return;
    }

    // Проверяем тип файла
    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите изображение');
      return;
    }

    // Проверяем размер файла (макс 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Размер файла не должен превышать 10MB');
      return;
    }

    setIsUploadingCover(true);
    try {
      const uploadedCover = await usersApi.uploadCover(profileUserId || user?.id || 0, file);
      setCoverImage(uploadedCover);
      
      // Небольшая задержка для проверки обновления URL
      setTimeout(() => {
        const url = getMediaUrl(uploadedCover, 'full');
      }, 100);
    } catch (error: any) {
      console.error('Ошибка загрузки cover image:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка загрузки изображения';
      alert(errorMessage);
    } finally {
      setIsUploadingCover(false);
      // Сбрасываем input, чтобы можно было загрузить тот же файл снова
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
        // Принудительно сбрасываем состояние input
        coverInputRef.current.blur();
        setTimeout(() => {
          if (coverInputRef.current) {
            coverInputRef.current.focus();
            coverInputRef.current.blur();
          }
        }, 10);
      }
    }
  };

  const handleUploadAvatar = () => {
    openFileInput(avatarInputRef.current);
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!profileUserId) return;
    
    // Если это свой профиль и аватарок нет, открываем диалог загрузки
    if (isOwnProfile && allAvatars.length === 0) {
      handleUploadAvatar();
      return;
    }
    
    // Если аватарок нет для просмотра, ничего не делаем
    if (allAvatars.length === 0) {
      return;
    }
    
    // Находим активную аватарку или первую, если активной нет
    const activeIndex = allAvatars.findIndex(a => a.isActive);
    const initialIndex = activeIndex >= 0 ? activeIndex : 0;
    
    setAvatarViewerIndex(initialIndex);
    setAvatarViewerSource(avatarRef.current);
    setShowAvatarViewer(true);
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isOwnProfile) return;
    const file = e.target.files?.[0];
    if (!file || !profileUserId) return;

    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите изображение');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Размер файла не должен превышать 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const uploadedAvatar = await usersApi.uploadAvatar(profileUserId || user?.id || 0, file);
      await loadProfileData(false, profileUserId); // Перезагружаем данные без skeleton
      setShowAvatarViewer(false);
    } catch (error: any) {
      console.error('Ошибка загрузки аватарки:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка загрузки изображения';
      alert(errorMessage);
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!isOwnProfile || !profileUserId || allAvatars.length === 0) return;
    
    const currentAvatar = allAvatars[avatarViewerIndex];
    if (!currentAvatar) return;

    if (!window.confirm('Вы уверены, что хотите удалить эту аватарку?')) {
      return;
    }

    try {
      await usersApi.deleteAvatar(profileUserId, currentAvatar.id);

      const remainingAvatars = allAvatars.filter(a => a.id !== currentAvatar.id);
      setAllAvatars(remainingAvatars);

      if (remainingAvatars.length === 0) {
        setAvatar(null);
        setShowAvatarViewer(false);
      } else {
        const newIndex = Math.min(avatarViewerIndex, remainingAvatars.length - 1);
        setAvatarViewerIndex(newIndex);
        const nextActiveAvatar = remainingAvatars.find(a => a.isActive) || remainingAvatars[newIndex];
        setAvatar(nextActiveAvatar);
      }

      void loadProfileData(false, profileUserId);
    } catch (error: any) {
      console.error('Ошибка удаления аватарки:', error);
      const message = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Ошибка удаления аватарки';
      alert(message);
    }
  };

  const handleActivateAvatar = async () => {
    if (!isOwnProfile || !profileUserId || allAvatars.length === 0) return;
    
    const currentAvatar = allAvatars[avatarViewerIndex];
    if (!currentAvatar || currentAvatar.isActive) return;

    try {
      await usersApi.activateAvatar(profileUserId, currentAvatar.id);
      await loadProfileData(false, profileUserId); // Перезагружаем данные без skeleton
    } catch (error: any) {
      console.error('Ошибка активации аватарки:', error);
      alert('Ошибка активации аватарки');
    }
  };

  const handleSaveBio = async (bio: string) => {
    if (!isOwnProfile || !profileUserId) return;
    
    try {
      // Сохраняем текст как есть (trim уже применен в EditBio)
      // Отправляем undefined вместо пустой строки, если bio пустая
      const bioToSave = bio || undefined;
      await usersApi.updateUserDetails(profileUserId, { bio: bioToSave });
      setProfileUser(prev => prev ? { ...prev, bio: bioToSave } : null);
      setShowEditBio(false);
      // Перезагружаем данные для обновления
      await loadProfileData(false, profileUserId);
    } catch (error: any) {
      console.error('Ошибка сохранения биографии:', error);
      throw error; // Пробрасываем ошибку в EditBio компонент
    }
  };

  const avatarUrl = getAvatarUrl(avatar, 'avatar');
  // Для cover: thumbnail (avatar) — быстрая загрузка, full — основное качество
  const coverThumbUrl = getMediaUrl(coverImage, 'avatar') || getMediaUrl(coverImage, 'small') || getMediaUrl(coverImage, 'full');
  const coverFullUrl = getMediaUrl(coverImage, 'full') || getMediaUrl(coverImage, 'avatar');
  const hasProgressiveCover = coverThumbUrl && coverFullUrl && coverThumbUrl !== coverFullUrl;

  // Если full изображение уже в кэше — onLoad может не сработать, проверяем complete
  useEffect(() => {
    if (!hasProgressiveCover || !coverFullUrl) return;
    const checkComplete = () => {
      const img = coverFullImgRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        setCoverFullLoaded(true);
      }
    };
    checkComplete();
    const id = requestAnimationFrame(() => checkComplete());
    return () => cancelAnimationFrame(id);
  }, [hasProgressiveCover, coverFullUrl]);

  const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
  
  const handleAvatarViewerIndexChange = useCallback((index: number) => {
    setAvatarViewerIndex(index);
  }, []);

  const avatarImageSourcesKey = allAvatars
    .map(a => {
      const full = getMediaUrl(a, 'full') || getMediaUrl(a, 'avatar') || '';
      const thumb = getMediaUrl(a, 'avatar') || full;
      return `${a.id}:${full}:${thumb}`;
    })
    .join('|');

  // Стабильные массивы URL — иначе ImageViewer сбрасывает progressive loading на каждый re-render
  const avatarImagesThumb = useMemo(
    () => allAvatars.map(a => getMediaUrl(a, 'avatar') || getMediaUrl(a, 'full') || '').filter(Boolean),
    [avatarImageSourcesKey, allAvatars]
  );
  const avatarImages = useMemo(
    () => allAvatars.map(a => getMediaUrl(a, 'full') || getMediaUrl(a, 'avatar') || '').filter(Boolean),
    [avatarImageSourcesKey, allAvatars]
  );
  
  // Убрали отладочные логи для production

  // Показываем полный скелетон страницы при первой загрузке
  if (isLoadingPosts && !hasLoadedOnceRef.current) {
    return (
      <div className={styles.profilePage}>
        <ProfilePageSkeleton />
      </div>
    );
  }

  return (
    <div className={styles.profilePage}>
      <div className={styles.profileContainer}>
        {/* Профиль пользователя */}
        <div className={styles.profileCard}>
          <div className={styles.coverImage}>
            {coverThumbUrl || coverFullUrl ? (
              hasProgressiveCover ? (
                <>
                  {/* Миниатюра (avatar) — грузится быстрее, отображается размытой */}
                  <img 
                    src={coverThumbUrl}
                    alt=""
                    className={`${styles.coverImageImg} ${styles.coverImageThumb}`}
                    aria-hidden
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  {/* Основное качество (full) — поверх миниатюры, плавно появляется */}
                  <img 
                    ref={coverFullImgRef}
                    src={coverFullUrl}
                    alt="Cover"
                    className={`${styles.coverImageImg} ${styles.coverImageFull} ${coverFullLoaded ? styles.coverImageFullVisible : ''}`}
                    onLoad={() => setCoverFullLoaded(true)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </>
              ) : (
                <img 
                  src={(coverFullUrl || coverThumbUrl) ?? undefined}
                  alt="Cover"
                  className={styles.coverImageImg}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              )
            ) : null}
            {isOwnProfile && (
              <>
                <input
                  id="profile-cover-upload"
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  onClick={(e) => {
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    if (target.value) {
                      target.value = '';
                    }
                  }}
                  className={styles.coverFileInput}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>
          
          <div className={styles.profileInfo}>
            <div className={styles.userHeader}>
              <div 
                ref={avatarRef}
                className={styles.avatarContainer}
                onClick={handleAvatarClick}
                style={{ cursor: allAvatars.length > 0 || isOwnProfile ? 'pointer' : 'default' }}
              >
                {avatarUrl ? (
                  <>
                    <img 
                      src={avatarUrl.startsWith('http') ? avatarUrl : `${apiBaseUrl}${avatarUrl}`}
                      alt={profileUser?.name || user?.name || 'Аватар'}
                      className={styles.avatar}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                    <div 
                      className={styles.avatarFallback}
                      style={{ 
                        backgroundColor: (profileUser?.id || user?.id) ? getAvatarColor(profileUser?.id || user?.id || 0) : '#4A90E2',
                        display: 'none'
                      }}
                    >
                      {getUserInitials(profileUser?.name || user?.name || 'U')}
                    </div>
                  </>
                ) : (
                  <div 
                    className={styles.avatarFallback}
                    style={{ 
                      backgroundColor: (profileUser?.id || user?.id) ? getAvatarColor(profileUser?.id || user?.id || 0) : '#4A90E2',
                      display: 'flex'
                    }}
                  >
                    {getUserInitials(profileUser?.name || user?.name || 'U')}
                  </div>
                )}
              </div>
              {isOwnProfile && (
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarFileChange}
                  style={{ display: 'none' }}
                />
              )}

              <div className={styles.userDetails}>
                <div className={styles.userNameAndBio}>
                  <h1 className={styles.userName}>{profileUser?.name || user?.name || 'User'}</h1>
                  
                  {isOwnProfile ? (
                    <div 
                      className={styles.userBioContainer}
                      onClick={() => setShowEditBio(true)}
                      title="Нажмите для редактирования"
                    >
                      <p className={styles.userBio}>
                        {profileUser?.bio || 'Несколько слов о себе'}
                      </p>
                      <button className={styles.bioEditButton} title="Редактировать подпись">
                        <i className="ri-edit-line"></i>
                      </button>
                    </div>
                  ) : (
                    <div className={styles.userBioContainer}>
                      <p className={styles.userBio}>
                        {profileUser?.bio || 'Несколько слов о себе'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.userStats}>
              <div className={styles.statItem}>
                <span className={styles.statValue}>{totalStats.posts}</span>
                <span className={styles.statLabel}>Постов</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statValue}>{totalStats.photos}</span>
                <span className={styles.statLabel}>Фото</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statValue}>{totalStats.likes}</span>
                <span className={styles.statLabel}>Лайков</span>
              </div>
              {isOwnProfile && (
                <button 
                  className={styles.addPostButton}
                  onClick={() => setShowCreatePost(true)}
                  title="Создать новый пост"
                >
                  <i className="ri-add-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Скролл объектов для фильтрации (все объекты из постов, не только из видимой выборки) */}
        <SitesScroll
          selectedSiteId={selectedSiteId}
          onSiteSelect={setSelectedSiteId}
          sites={allSitesFromPosts}
        />
        {/* Разделитель после фильтра */}
        <hr className={styles.filterDivider} />

        {/* Сетка с фотографиями (как в Instagram) */}
        <div className={styles.wallContainer}>
          {isOwnProfile && (
            <>
              <BottomSheet
                isOpen={showCreatePost}
                onClose={() => setShowCreatePost(false)}
                title="Создать новый пост"
                size="large"
              >
                <CreatePost
                  onCreatePost={async (content, images, siteId) => {
                    try {
                      await handleCreatePost(content, images, siteId);
                      setShowCreatePost(false);
                    } catch (error) {
                      // Ошибка уже залогирована в handleCreatePost
                    }
                  }}
                  onCancel={() => setShowCreatePost(false)}
                  preselectedSiteId={selectedSiteId}
                />
              </BottomSheet>

              <BottomSheet
                isOpen={showEditBio}
                onClose={() => setShowEditBio(false)}
                title="Немного о себе"
                size="auto"
                allowSwipeToExpand={false}
              >
                <EditBio
                  initialBio={profileUser?.bio || ''}
                  onSave={handleSaveBio}
                  onCancel={() => setShowEditBio(false)}
                />
              </BottomSheet>

              <BottomSheet
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                title="Личный профиль"
                size="large"
              >
                <ProfileSettings 
                  onClose={() => setShowSettings(false)} 
                  isUploadingCover={isUploadingCover}
                />
              </BottomSheet>
            </>
          )}

          {/* Сетка фотографий */}
          {postsWithImages.length === 0 ? (
            <div className={styles.emptyState}>
              <i className="ri-image-line"></i>
              <p>{selectedSiteId ? 'Нет фотографий для выбранного объекта' : 'Пока нет фотографий'}</p>
              <span>{selectedSiteId ? 'Выберите другой объект или "Все"' : 'Создайте пост с фотографиями!'}</span>
            </div>
          ) : (
            <>
              <PhotoGrid
                posts={postsWithImages}
                onPostClick={handleGridPostClick}
              />
              {/* Триггер для загрузки дополнительных постов */}
              {hasMorePosts && (
                <div ref={loadMoreTriggerRef} className={styles.loadMoreTrigger}>
                  {isLoadingMoreRef.current && (
                    <div className={styles.loadingMore}>
                      <i className="ri-loader-4-line"></i>
                      <span>Загрузка...</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Полноэкранная лента постов (посты одного объекта) */}
      {showPostsFeed && feedInitialPostId && feedPosts.length > 0 && (
        <PostsFeed
          posts={feedPosts}
          initialPostId={feedInitialPostId}
          onClose={handleCloseFeed}
          onLike={handleLikePost}
          onDelete={handleDeletePost}
          currentUserId={user?.id || 0}
          siteName={feedSiteName}
          hideMenuButton
          loadPostsUserId={profileUserId} // Загружаем посты пользователя
          loadPostsSiteId={feedSiteId ?? undefined} // Фильтруем по объекту
        />
      )}

      {/* Слайдер аватарок */}
      {showAvatarViewer && allAvatars.length > 0 && avatarImages.length > 0 && (
        <ImageViewer
          images={avatarImages}
          imagesThumb={avatarImagesThumb}
          initialIndex={avatarViewerIndex}
          onClose={() => setShowAvatarViewer(false)}
          sourceElement={avatarViewerSource}
          onIndexChange={handleAvatarViewerIndexChange}
          showActions={isOwnProfile}
          actions={(() => {
            const actionsList: ImageViewerAction[] = [];
            
            if (isOwnProfile) {
              actionsList.push({
                icon: 'ri-upload-cloud-line',
                label: 'Загрузить новую',
                onClick: handleUploadAvatar,
                variant: 'primary' as const
              });
              
              if (allAvatars[avatarViewerIndex] && !allAvatars[avatarViewerIndex].isActive) {
                actionsList.push({
                  icon: 'ri-check-line',
                  label: 'Сделать активной',
                  onClick: handleActivateAvatar,
                  variant: 'default' as const
                });
              }
              
              actionsList.push({
                icon: 'ri-delete-bin-line',
                label: 'Удалить',
                onClick: handleDeleteAvatar,
                variant: 'danger' as const
              });
            }
            
            return actionsList;
          })()}
        />
      )}

      {!hideBottomNavigation && (
        <BottomNavigation items={navItems} />
      )}
    </div>
  );
};

export default ProfilePage;

